<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import logo from '../../assets/home/logo.webp';
import { useAuthStore } from '../../stores/auth';
import { useCartStore } from '../../stores/cart';
import { useWalletStore } from '../../stores/wallet';

const { t } = useI18n();
const route = useRoute();
const auth = useAuthStore();
const cart = useCartStore();
const wallet = useWalletStore();

// Load the real wallet balance once the viewer is authenticated (and react to login).
watch(() => auth.isAuthenticated, (yes) => { if (yes) wallet.ensureLoaded(); }, { immediate: true });

// Terminal token treatment: UPPER_SNAKE, locale-safe ("Log In" → "LOG_IN").
const term = (s: string) => s.toUpperCase().replace(/ /g, '_');

const navLinks = [
  { key: 'catalog', to: '/catalog' },
  { key: 'articles', to: '/articles' },
  { key: 'faq', to: '/faq' },
];
const isActive = (to: string) =>
  to === '/' ? route.path === '/' : route.path.startsWith(to);

const accountLinks = [
  { key: 'dashboard', to: '/account' },
  { key: 'orders', to: '/account/orders' },
  { key: 'favorites', to: '/account/favorites' },
  { key: 'settings', to: '/account/settings' },
  { key: 'billing', to: '/account/billing' },
];

const userHandle = computed(() => term(auth.displayName.split('@')[0] || 'user'));
const userInitial = computed(() => (auth.displayName[0] || 'U').toUpperCase());

// User-chip dropdown (close on outside click / Escape).
const menuOpen = ref(false);
const chipRef = ref<HTMLElement | null>(null);
function onDocClick(e: MouseEvent) {
  if (menuOpen.value && chipRef.value && !chipRef.value.contains(e.target as Node)) {
    menuOpen.value = false;
  }
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') menuOpen.value = false;
}
onMounted(() => {
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onKey);
});

async function logout() {
  menuOpen.value = false;
  await auth.logout();
}
</script>

<template>
  <header
    class="relative z-30 flex h-16 items-center justify-between border-b border-term-line bg-term-bg px-8 font-mono text-term-ink"
  >
    <!-- Left: Brand -->
    <router-link to="/" class="flex shrink-0 items-center" aria-label="PixelKey">
      <img :src="logo" alt="PixelKey" class="h-7 w-auto" />
    </router-link>

    <!-- Center: Navigation -->
    <nav class="flex h-full items-center gap-8">
      <template v-for="(link, i) in navLinks" :key="link.key">
        <span v-if="i > 0" class="select-none text-term-line" aria-hidden="true">·</span>
        <router-link
          :to="link.to"
          class="flex h-full items-center border-b-2 pb-0.5 text-[14px] tracking-[0.05em] uppercase transition-colors"
          :class="
            isActive(link.to)
              ? 'border-term-green font-bold text-term-green'
              : 'border-transparent font-medium text-term-ink hover:text-term-green'
          "
        >
          {{ term(t(`nav.${link.key}`)) }}
        </router-link>
      </template>
    </nav>

    <!-- Right: Actions -->
    <div class="flex shrink-0 items-center gap-6">
      <!-- Authenticated-only: wallet pill -->
      <router-link
        v-if="auth.isAuthenticated"
        to="/account/billing"
        class="flex h-8 items-center gap-2 border border-term-green bg-term-green/5 px-4 text-[14px] font-medium text-term-green transition-colors hover:bg-term-green/10"
      >
        <svg viewBox="0 0 13 12" fill="none" class="size-[13px]">
          <rect x="0.75" y="1.25" width="11.5" height="9.5" rx="1.5" stroke="currentColor" stroke-width="1.3" />
          <path d="M9 6h2.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" />
        </svg>
        {{ wallet.formatted }}
      </router-link>

      <!-- Cart -->
      <router-link
        to="/cart"
        class="flex items-center gap-2 text-[14px] font-medium text-term-ink transition-colors hover:text-term-green"
      >
        <svg viewBox="0 0 17 17" fill="none" class="size-[16px]">
          <path d="M1 1h2.2l1.9 9.8h8.4l1.4-6.6H4.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />
          <circle cx="6.5" cy="14.6" r="1.2" fill="currentColor" />
          <circle cx="13.2" cy="14.6" r="1.2" fill="currentColor" />
        </svg>
        <span><span class="text-term-danger">[{{ cart.count }}]</span> {{ term(t('common.cart')) }}</span>
      </router-link>

      <!-- Search -->
      <button type="button" class="text-term-ink transition-colors hover:text-term-green" :aria-label="t('common.search')">
        <svg viewBox="0 0 15 15" fill="none" class="size-[15px]">
          <circle cx="6.4" cy="6.4" r="5.1" stroke="currentColor" stroke-width="1.4" />
          <path d="M10.4 10.4 14 14" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
        </svg>
      </button>

      <!-- Guest: log in / sign up -->
      <template v-if="!auth.isAuthenticated">
        <span class="h-6 w-px bg-term-line" aria-hidden="true" />
        <router-link to="/login" class="text-[14px] font-medium text-term-green transition-colors hover:brightness-110">
          {{ term(t('common.logIn')) }}
        </router-link>
        <router-link
          to="/register"
          class="flex h-8 items-center justify-center bg-term-green px-6 text-[14px] font-bold text-term-green-ink transition-colors hover:brightness-110"
        >
          [ {{ term(t('common.signUp')) }} ]
        </router-link>
      </template>

      <!-- Authenticated: user chip + dropdown -->
      <template v-else>
        <span class="h-6 w-px bg-term-line" aria-hidden="true" />
        <div ref="chipRef" class="relative">
          <button
            type="button"
            class="flex items-center gap-2 transition-opacity hover:opacity-90"
            :aria-expanded="menuOpen"
            aria-haspopup="menu"
            @click="menuOpen = !menuOpen"
          >
            <span class="flex size-7 items-center justify-center bg-term-line text-[12px] font-bold text-term-green">
              {{ userInitial }}
            </span>
            <span class="text-[14px] font-medium text-term-ink">{{ userHandle }}</span>
            <svg viewBox="0 0 8 5" fill="none" class="h-[5px] w-2 text-term-muted transition-transform" :class="{ 'rotate-180': menuOpen }">
              <path d="M1 1l3 3 3-3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>

          <div
            v-if="menuOpen"
            role="menu"
            class="absolute right-0 z-40 mt-3 min-w-[180px] border border-term-line bg-term-bg-2 py-2 shadow-lg"
          >
            <router-link
              v-for="link in accountLinks"
              :key="link.key"
              :to="link.to"
              role="menuitem"
              class="block px-4 py-2 text-[12px] uppercase tracking-[0.05em] text-term-ink transition-colors hover:bg-term-green/10 hover:text-term-green"
              @click="menuOpen = false"
            >
              {{ term(t(`nav.${link.key}`)) }}
            </router-link>
            <div class="my-1 h-px bg-term-line" />
            <button
              type="button"
              role="menuitem"
              class="block w-full px-4 py-2 text-left text-[12px] uppercase tracking-[0.05em] text-term-muted transition-colors hover:bg-term-green/10 hover:text-term-green"
              @click="logout"
            >
              {{ term(t('common.logOut')) }}
            </button>
          </div>
        </div>
      </template>
    </div>
  </header>
</template>
